import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import {
  getArticleById,
  getRelatedArticles,
  categories,
} from '@/mocks/data';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '0', 10);
  const article = getArticleById(articleId);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 爱情岛论坛独家提供`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', article.excerpt);
      }
      const metaKw = document.querySelector('meta[name="keywords"]');
      if (metaKw) {
        metaKw.setAttribute(
          'content',
          `爱情岛论坛独家提供,${article.tags.join(',')},爱情岛独家资源`
        );
      }
    }
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-20 text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200/70">
            <i className="ri-file-unknow-line text-foreground-400 text-2xl"></i>
          </div>
          <h1 className="text-xl font-heading font-semibold text-foreground-900 mb-2">
            文章未找到
          </h1>
          <p className="text-sm text-foreground-500 mb-6">
            您访问的文章不存在或已被移除
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const cat = categories.find((c) => c.id === article.categoryId);
  const relatedArticles = getRelatedArticles(article.id, article.categoryId);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: article.categoryName, href: `/category/${article.categoryId}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishedAt,
    dateModified: article.publishedAt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '爱情岛论坛',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.aiqingdao.com/article/${article.id}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <article>
              {/* Cover Image */}
              <div className="relative aspect-[16/9] rounded-lg overflow-hidden mb-8">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  title={`${article.title} - 爱情岛论坛独家提供`}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Article Header */}
              <header className="mb-8">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {cat && (
                    <Link
                      to={`/category/${cat.slug}`}
                      className="px-2.5 py-1 rounded-md text-xs font-medium bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors whitespace-nowrap"
                    >
                      {cat.name}
                    </Link>
                  )}
                  {article.tags.map((tag, i) => (
                    <span
                      key={i}
                      className="px-2.5 py-1 rounded-md text-xs bg-background-200/70 text-foreground-500"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                  <div className="flex items-center gap-2">
                    <img
                      src={article.authorAvatar}
                      alt={article.author}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <span className="font-medium text-foreground-700">
                      {article.author}
                    </span>
                  </div>
                  <span className="text-foreground-300">|</span>
                  <span>{article.publishedAt}</span>
                  <span className="text-foreground-300">|</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.readCount > 10000
                      ? `${(article.readCount / 10000).toFixed(1)}万 阅读`
                      : `${article.readCount} 阅读`}
                  </span>
                </div>
              </header>

              {/* Article Content */}
              <div
                className="prose prose-lg max-w-none mb-12 text-foreground-800 leading-relaxed article-content"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Article Footer */}
              <div className="flex flex-wrap items-center justify-between py-6 border-t border-b border-background-200/70 mb-10">
                <div className="flex items-center gap-4">
                  <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-heart-line"></i>
                    点赞 {article.likeCount}
                  </button>
                  <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-bookmark-line"></i>
                    收藏
                  </button>
                </div>
                <div className="flex items-center gap-2 mt-3 sm:mt-0">
                  <span className="text-sm text-foreground-400">分享到：</span>
                  <a
                    href="#"
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-50 hover:text-primary-500 transition-colors"
                    aria-label="分享到微信"
                  >
                    <i className="ri-wechat-line text-sm"></i>
                  </a>
                  <a
                    href="#"
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-50 hover:text-primary-500 transition-colors"
                    aria-label="分享到微博"
                  >
                    <i className="ri-weibo-line text-sm"></i>
                  </a>
                  <a
                    href="#"
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-50 hover:text-primary-500 transition-colors"
                    aria-label="复制链接"
                  >
                    <i className="ri-link text-sm"></i>
                  </a>
                </div>
              </div>

              {/* Related Articles */}
              {relatedArticles.length > 0 && (
                <section className="mb-16">
                  <h3 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                    相关推荐
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {relatedArticles.map((related) => (
                      <Link
                        key={related.id}
                        to={`/article/${related.id}`}
                        className="group flex gap-4 bg-background-100/70 rounded-lg p-4 hover:bg-background-100 transition-colors"
                      >
                        <img
                          src={related.coverImage}
                          alt={related.title}
                          className="w-24 h-24 md:w-28 md:h-28 rounded-md object-cover flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm md:text-base font-heading font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug mb-1.5">
                            {related.title}
                          </h4>
                          <p className="text-xs text-foreground-500 line-clamp-1 mb-2">
                            {related.excerpt}
                          </p>
                          <span className="text-xs text-foreground-400">
                            {related.publishedAt}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </article>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}